import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
	Briefcase,
	Edit,
	ExternalLink,
	Globe,
	Plus,
	Search,
	Trash2,
	X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import type { Service, ServiceNotificationSettings } from "../types";
import { isAdminRole } from "../utils/auth";
import { getContentFieldProps } from "../utils/textDirection";
import { PageHeader } from "./PageHeader";
import { SettingsGroup } from "./SettingsGroup";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Textarea } from "./ui/textarea";

const DEFAULT_NOTIFICATION_FROM_NAME = "{{ service.name }} - Agent";
const DEFAULT_NOTIFICATION_SUBJECT =
	"New call from {{ callerNumber }} about {{ callSubject }}";
const DEFAULT_NOTIFICATION_BODY =
	"A new call from {{ callerNumber }} about {{ callSubject }} was just handled.\n\n{{ linkToCall }}";

function defaultServiceNotifications(): ServiceNotificationSettings {
	return {
		enabled: true,
		emails: [],
		fromName: DEFAULT_NOTIFICATION_FROM_NAME,
		subject: DEFAULT_NOTIFICATION_SUBJECT,
		body: DEFAULT_NOTIFICATION_BODY,
	};
}

function normalizeServiceNotifications(
	notifications: Service["notifications"],
): ServiceNotificationSettings {
	const defaults = defaultServiceNotifications();
	if (!notifications || typeof notifications !== "object") {
		return defaults;
	}

	return {
		enabled:
			typeof notifications.enabled === "boolean"
				? notifications.enabled
				: defaults.enabled,
		emails: Array.isArray(notifications.emails)
			? notifications.emails
			: defaults.emails,
		fromName: notifications.fromName || defaults.fromName,
		subject: notifications.subject || defaults.subject,
		body: notifications.body || defaults.body,
	};
}

function createEmptyService(companyId: string): Service {
	return {
		id: `service-${Date.now()}`,
		companyId,
		name: "",
		description: "",
		website: "",
		serviceBrief: "",
		contactDetails: "",
		faq: "",
		notifications: defaultServiceNotifications(),
		knowledgeBase: "",
		additionalResources: [],
		tags: [],
	};
}

function normalizeService(service: Service): Service {
	return {
		...service,
		description: service.description || "",
		website: service.website || "",
		serviceBrief: service.serviceBrief || "",
		contactDetails: service.contactDetails || "",
		faq: service.faq || "",
		notifications: normalizeServiceNotifications(service.notifications),
		knowledgeBase: service.knowledgeBase || "",
		additionalResources: service.additionalResources || [],
		tags: service.tags || [],
	};
}

export function ServicesPage() {
	const { i18n } = useLingui();
	const {
		user,
		services,
		language,
		createService,
		updateService,
		deleteService,
	} = useData();
	const canManage = isAdminRole(user?.role);
	const isRtl = language === "he";
	const fieldGroupClassName = "grid gap-2 text-start";
	const fieldLabelClassName = isRtl
		? "justify-start text-right"
		: "justify-start text-left";
	const [searchQuery, setSearchQuery] = useState("");
	const [editorMode, setEditorMode] = useState<"create" | "edit" | null>(null);
	const [editorService, setEditorService] = useState<Service | null>(null);
	const contentFieldProps = (
		value: string | null | undefined,
		extraClassName = "",
	) => getContentFieldProps(value, isRtl ? "rtl" : "ltr", extraClassName);
	const [serviceToDelete, setServiceToDelete] = useState<Service | null>(null);
	const [isSaving, setIsSaving] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	const filteredServices = useMemo(() => {
		const query = searchQuery.trim().toLowerCase();
		if (!query) return services;

		return services.filter((service) =>
			[
				service.name,
				service.description,
				service.serviceBrief,
				service.website,
				service.faq,
				service.knowledgeBase,
			]
				.filter(Boolean)
				.some((value) => value!.toLowerCase().includes(query)),
		);
	}, [searchQuery, services]);

	const openCreateDialog = () => {
		if (!user?.companyId) {
			toast.error(i18n._("serviceEditor.toasts.missingCompany"));
			return;
		}

		setEditorMode("create");
		setEditorService(createEmptyService(user.companyId));
	};

	const openEditDialog = (service: Service) => {
		setEditorMode("edit");
		setEditorService(normalizeService(service));
	};

	const closeDialog = () => {
		setEditorMode(null);
		setEditorService(null);
		setIsSaving(false);
	};

	const closeDeleteDialog = () => {
		setServiceToDelete(null);
		setIsDeleting(false);
	};

	const saveService = async () => {
		if (!editorService) return;
		if (!editorService.name.trim()) {
			toast.error(i18n._("serviceEditor.fields.nameRequired"));
			return;
		}

		setIsSaving(true);
		try {
			const notifications = normalizeServiceNotifications(
				editorService.notifications,
			);
			const normalized = {
				...editorService,
				name: editorService.name.trim(),
				description: editorService.description.trim(),
				website: editorService.website?.trim() || "",
				serviceBrief: editorService.serviceBrief?.trim() || "",
				contactDetails: editorService.contactDetails?.trim() || "",
				faq: editorService.faq?.trim() || "",
				notifications: {
					...notifications,
					emails: notifications.emails
						.map((email) => email.trim())
						.filter(Boolean),
					fromName: notifications.fromName.trim(),
					subject: notifications.subject.trim(),
					body: notifications.body.trim(),
				},
				knowledgeBase: editorService.knowledgeBase?.trim() || "",
				additionalResources: editorService.additionalResources || [],
			};
			const { tags: _tags, ...servicePayload } = normalized;

			if (editorMode === "create") {
				await createService(servicePayload);
			} else {
				await updateService(servicePayload);
			}

			closeDialog();
		} catch (error) {
			console.error("Failed saving service", error);
			toast.error(i18n._("serviceEditor.saveFailed"));
			setIsSaving(false);
		}
	};

	const handleDeleteService = async () => {
		if (!serviceToDelete?.id) return;

		setIsDeleting(true);
		try {
			await deleteService(serviceToDelete.id);
			closeDeleteDialog();
		} catch (error) {
			console.error("Failed deleting service", error);
			toast.error(i18n._("serviceEditor.deleteFailed"));
			setIsDeleting(false);
		}
	};

	const editorTitle =
		editorMode === "create"
			? i18n._("serviceEditor.dialog.create")
			: i18n._("serviceEditor.dialog.edit");
	return (
		<div
			className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6"
			data-testid="services-page"
		>
			<PageHeader
				className={
					editorMode ? "hidden" : "sm:flex-row sm:items-end sm:justify-between"
				}
				title={t({ id: "services", message: "Services" })}
				description={
					canManage
						? t({
								id: "servicesPageSubtitle",
								message: "Manage the business context your agents rely on",
							})
						: t({
								id: "servicesPageReadOnlySubtitle",
								message: "View the business context your agents rely on",
							})
				}
				actions={
					canManage ? (
						<>
							<Button
								onClick={openCreateDialog}
								className="w-full gap-2 sm:w-auto"
							>
								<Plus className="h-4 w-4" />
								{t({ id: "createService", message: "Create Service" })}
							</Button>
						</>
					) : null
				}
			/>

			{!editorMode && (
				<div className="relative min-w-0 w-full">
					<Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						value={searchQuery}
						onChange={(event) => setSearchQuery(event.target.value)}
						placeholder={t({
							id: "searchServicesShort",
							message: "Search services...",
						})}
						{...contentFieldProps(searchQuery, "ps-10")}
					/>
				</div>
			)}

			<div className={editorMode ? "hidden" : "space-y-4"}>
				{filteredServices.map((service) => (
					<Card key={service.id} className="w-full">
						<CardContent className="p-0 [&:last-child]:pb-0">
							<div className="border-b border-card-border p-6">
								<div className="flex items-start gap-3">
									<Briefcase className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
									<div className="min-w-0">
										<div className="text-lg font-semibold">{service.name}</div>
										{service.website ? (
											<a
												href={service.website}
												target="_blank"
												rel="noreferrer"
												className="mt-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
											>
												<Globe className="h-3.5 w-3.5" />
												<span className="break-all">{service.website}</span>
												<ExternalLink className="h-3.5 w-3.5" />
											</a>
										) : null}
									</div>
								</div>
							</div>
							<div className="space-y-4 border-b border-card-border px-6 py-4">
								<div className="space-y-2">
									<p
										className="whitespace-pre-wrap break-words text-sm leading-6 text-foreground/80"
										dir="auto"
									>
										{service.serviceBrief ||
											t({
												id: "noServiceBrief",
												message: "No service brief added yet.",
											})}
									</p>
								</div>
								<div className="flex flex-wrap gap-2">
									<Badge
										variant="outline"
										className={
											service.faq?.trim()
												? "border-live/30 bg-live-tint text-live"
												: "border-warn/30 bg-warn-tint text-foreground"
										}
									>
										{service.faq?.trim()
											? t({ id: "services.faqPresent", message: "FAQ added" })
											: t({
													id: "services.faqMissing",
													message: "FAQ missing",
												})}
									</Badge>
									<Badge
										variant="outline"
										className={
											service.knowledgeBase?.trim()
												? "border-live/30 bg-live-tint text-live"
												: "border-warn/30 bg-warn-tint text-foreground"
										}
									>
										{service.knowledgeBase?.trim()
											? t({
													id: "services.knowledgePresent",
													message: "Knowledge base added",
												})
											: t({
													id: "services.knowledgeMissing",
													message: "Knowledge base missing",
												})}
									</Badge>
								</div>
							</div>
							{canManage ? (
								<div className="px-6 py-4">
									<div className="flex flex-wrap gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => openEditDialog(service)}
											className="gap-2"
										>
											<Edit className="h-4 w-4" />
											{t({ id: "edit", message: "Edit" })}
										</Button>
										<Button
											variant="destructive"
											size="sm"
											onClick={() => setServiceToDelete(service)}
											className="gap-2"
										>
											<Trash2 className="h-4 w-4" />
											{t({ id: "delete", message: "Delete" })}
										</Button>
									</div>
								</div>
							) : null}
						</CardContent>
					</Card>
				))}
			</div>

			{editorMode === null && filteredServices.length === 0 ? (
				<Card className="border-dashed">
					<CardContent className="py-16 text-center">
						<Briefcase className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
						<p className="text-sm text-muted-foreground">
							{t({
								id: "searchServices",
								message:
									"Search services by name, website, FAQ, or knowledge base...",
							})}
						</p>
					</CardContent>
				</Card>
			) : null}

			<Dialog
				modal={false}
				open={editorMode !== null}
				onOpenChange={(open) => (!open ? closeDialog() : null)}
			>
				<DialogContent
					inline
					dir={isRtl ? "rtl" : "ltr"}
					showClose={false}
					className="flex h-[calc(100vh-8rem)] min-h-[680px] max-w-none flex-col overflow-hidden border-0 bg-transparent p-0 text-start shadow-none"
				>
					<div className="rounded-lg border-[1.5px] border-card-border bg-card p-4 shadow-card sm:p-6 space-y-4">
						<div className="flex flex-wrap items-start justify-between gap-3">
							<DialogHeader className="text-start">
								<DialogTitle className="text-xl font-semibold">
									{editorTitle}
								</DialogTitle>
								<DialogDescription>
									{i18n._("serviceEditor.subtitle")}
								</DialogDescription>
							</DialogHeader>
							<div className="flex items-center gap-2">
								<Button
									variant="outline"
									onClick={closeDialog}
									disabled={isSaving}
								>
									{i18n._("common.cancel")}
								</Button>
								<Button onClick={saveService} disabled={isSaving}>
									{isSaving
										? i18n._("common.saving")
										: editorMode === "create"
											? i18n._("common.create")
											: i18n._("common.save")}
								</Button>
							</div>
						</div>
						<div className="rounded-lg border border-ring/30 bg-accent px-4 py-3 text-sm text-foreground">
							<p className="font-semibold">
								{i18n._({
									id: "serviceEditor.changeEffect.title",
									message: "Business changes require agent redeployment",
								})}
							</p>
							<p className="mt-1 leading-5 text-muted-foreground">
								{i18n._({
									id: "serviceEditor.changeEffect.description",
									message:
										"Linked agents use this business information after their next deployment to Testing or Live.",
								})}
							</p>
						</div>
					</div>
					{editorService ? (
						<Tabs
							dir={isRtl ? "rtl" : "ltr"}
							defaultValue="general"
							className="grid min-h-0 flex-1 gap-4 overflow-hidden py-2 lg:grid-cols-[220px_minmax(0,1fr)]"
						>
							<TabsList
								dir={isRtl ? "rtl" : "ltr"}
								className="grid h-auto w-full grid-cols-3 lg:self-start lg:flex lg:flex-col lg:items-stretch lg:gap-1 lg:bg-transparent lg:p-0"
							>
								<TabsTrigger
									value="general"
									className="lg:flex-none lg:justify-start lg:px-4 lg:py-3 lg:whitespace-nowrap"
								>
									{t({
										id: "serviceEditor.tabs.overview",
										message: "Overview",
									})}
								</TabsTrigger>
								<TabsTrigger
									value="basic"
									className="lg:flex-none lg:justify-start lg:px-4 lg:py-3 lg:whitespace-nowrap"
								>
									{t({
										id: "serviceEditor.tabs.basicKnowledge",
										message: "Basic knowledge",
									})}
								</TabsTrigger>
								<TabsTrigger
									value="knowledge"
									className="lg:flex-none lg:justify-start lg:px-4 lg:py-3 lg:whitespace-nowrap"
								>
									{t({
										id: "serviceEditor.tabs.knowledgeBase",
										message: "Knowledge base",
									})}
								</TabsTrigger>
							</TabsList>

							<div className="min-h-0 min-w-0 flex-1 overflow-y-auto p-1 text-start">
								<TabsContent value="general" className="mt-0 space-y-6">
									<SettingsGroup
										header={
											<h3>
												{t({
													id: "serviceEditor.tabs.overview",
													message: "Overview",
												})}
											</h3>
										}
										description={t({
											id: "serviceEditor.groupHelp.overview",
											message:
												"Set the service name, website, description, and contact details.",
										})}
									>
										<div className={fieldGroupClassName}>
											<Label
												htmlFor="service-name"
												className={fieldLabelClassName}
											>
												{t({ id: "name", message: "Name" })}
											</Label>
											<Input
												id="service-name"
												value={editorService.name}
												onChange={(event) =>
													setEditorService((current) =>
														current
															? { ...current, name: event.target.value }
															: current,
													)
												}
												{...contentFieldProps(editorService.name)}
											/>
										</div>

										<div className={fieldGroupClassName}>
											<Label
												htmlFor="service-website"
												className={fieldLabelClassName}
											>
												{t({ id: "website", message: "Website" })}
											</Label>
											<Input
												id="service-website"
												dir="ltr"
												value={editorService.website || ""}
												onChange={(event) =>
													setEditorService((current) =>
														current
															? { ...current, website: event.target.value }
															: current,
													)
												}
												className="text-left"
											/>
										</div>

										<div className={fieldGroupClassName}>
											<Label
												htmlFor="service-description"
												className={fieldLabelClassName}
											>
												{t({ id: "description", message: "Description" })}
											</Label>
											<Textarea
												id="service-description"
												value={editorService.description}
												onChange={(event) =>
													setEditorService((current) =>
														current
															? { ...current, description: event.target.value }
															: current,
													)
												}
												{...contentFieldProps(editorService.description)}
											/>
										</div>

										<div className={fieldGroupClassName}>
											<Label
												htmlFor="service-contact"
												className={fieldLabelClassName}
											>
												{t({
													id: "contactDetails",
													message: "Contact Details",
												})}
											</Label>
											<Textarea
												id="service-contact"
												value={editorService.contactDetails || ""}
												onChange={(event) =>
													setEditorService((current) =>
														current
															? {
																	...current,
																	contactDetails: event.target.value,
																}
															: current,
													)
												}
												{...contentFieldProps(editorService.contactDetails)}
											/>
										</div>
									</SettingsGroup>
								</TabsContent>

								<TabsContent value="basic" className="mt-0 space-y-6">
									<SettingsGroup
										header={<h3>{i18n._("serviceEditor.serviceBrief")}</h3>}
										description={t({
											id: "serviceEditor.groupHelp.brief",
											message:
												"Describe the business and the services your agents represent.",
										})}
									>
										<div className={fieldGroupClassName}>
											<Label
												htmlFor="service-brief"
												className={fieldLabelClassName}
											>
												{i18n._("serviceEditor.serviceBrief")}
											</Label>
											<Textarea
												id="service-brief"
												rows={5}
												value={editorService.serviceBrief || ""}
												onChange={(event) =>
													setEditorService((current) =>
														current
															? { ...current, serviceBrief: event.target.value }
															: current,
													)
												}
												{...contentFieldProps(editorService.serviceBrief)}
											/>
										</div>
									</SettingsGroup>
									<SettingsGroup
										header={<h3>{i18n._("serviceEditor.faq")}</h3>}
										description={t({
											id: "serviceEditor.groupHelp.faq",
											message:
												"Provide answers to common questions for use in every conversation.",
										})}
									>
										<div className={fieldGroupClassName}>
											<Label
												htmlFor="service-faq"
												className={fieldLabelClassName}
											>
												{i18n._("serviceEditor.faq")}
											</Label>
											<Textarea
												id="service-faq"
												rows={8}
												value={editorService.faq || ""}
												onChange={(event) =>
													setEditorService((current) =>
														current
															? { ...current, faq: event.target.value }
															: current,
													)
												}
												{...contentFieldProps(editorService.faq)}
											/>
										</div>
									</SettingsGroup>
								</TabsContent>

								<TabsContent value="knowledge" className="mt-0 space-y-6">
									<SettingsGroup
										header={<h3>{i18n._("serviceEditor.knowledgeBase")}</h3>}
										description={t({
											id: "serviceEditor.groupHelp.knowledge",
											message:
												"Add reference information the agent can search when it needs more detail.",
										})}
									>
										<div className="flex min-h-0 flex-1 flex-col gap-2">
											<Label
												htmlFor="service-knowledge"
												className={fieldLabelClassName}
											>
												{i18n._("serviceEditor.knowledgeBase")}
											</Label>
											<Textarea
												id="service-knowledge"
												value={editorService.knowledgeBase || ""}
												onChange={(event) =>
													setEditorService((current) =>
														current
															? {
																	...current,
																	knowledgeBase: event.target.value,
																}
															: current,
													)
												}
												{...contentFieldProps(
													editorService.knowledgeBase,
													"min-h-64",
												)}
											/>
										</div>
									</SettingsGroup>
								</TabsContent>

								<TabsContent value="notifications" className="mt-0 space-y-6">
									<div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto text-start">
										<div
											className={`flex items-center justify-between gap-3 rounded-lg border bg-slate-50/60 p-4 ${isRtl ? "flex-row-reverse" : ""}`}
										>
											<div className="text-start">
												<Label
													htmlFor="service-notifications-enabled"
													className={fieldLabelClassName}
												>
													{i18n._("serviceEditor.notifications.enabled")}
												</Label>
												<p className="text-xs text-muted-foreground">
													{i18n._("serviceEditor.notifications.enabledHelp")}
												</p>
											</div>
											<Switch
												id="service-notifications-enabled"
												checked={
													normalizeServiceNotifications(
														editorService.notifications,
													).enabled
												}
												onCheckedChange={(checked) =>
													setEditorService((current) =>
														current
															? {
																	...current,
																	notifications: {
																		...normalizeServiceNotifications(
																			current.notifications,
																		),
																		enabled: checked,
																	},
																}
															: current,
													)
												}
											/>
										</div>

										<div className={fieldGroupClassName}>
											<Label className={fieldLabelClassName}>
												{i18n._("serviceEditor.notifications.emails")}
											</Label>
											<Button
												type="button"
												variant="outline"
												size="sm"
												className="w-fit"
												onClick={() =>
													setEditorService((current) => {
														if (!current) return current;
														const notifications = normalizeServiceNotifications(
															current.notifications,
														);
														return {
															...current,
															notifications: {
																...notifications,
																emails: ["", ...notifications.emails],
															},
														};
													})
												}
											>
												<Plus className="mr-2 h-4 w-4 rtl:mr-0 rtl:ml-2" />
												{i18n._("serviceEditor.notifications.addEmail")}
											</Button>
											<div className="space-y-2">
												{normalizeServiceNotifications(
													editorService.notifications,
												).emails.map((email, index) => (
													<div
														key={index}
														className={`flex items-center gap-2 ${isRtl ? "flex-row-reverse" : ""}`}
													>
														<Input
															type="email"
															dir="ltr"
															value={email}
															onChange={(event) =>
																setEditorService((current) => {
																	if (!current) return current;
																	const notifications =
																		normalizeServiceNotifications(
																			current.notifications,
																		);
																	const emails = [...notifications.emails];
																	emails[index] = event.target.value;
																	return {
																		...current,
																		notifications: { ...notifications, emails },
																	};
																})
															}
															className="text-left"
														/>
														<Button
															type="button"
															variant="ghost"
															size="sm"
															onClick={() =>
																setEditorService((current) => {
																	if (!current) return current;
																	const notifications =
																		normalizeServiceNotifications(
																			current.notifications,
																		);
																	return {
																		...current,
																		notifications: {
																			...notifications,
																			emails: notifications.emails.filter(
																				(_, emailIndex) => emailIndex !== index,
																			),
																		},
																	};
																})
															}
														>
															<X className="h-4 w-4" />
														</Button>
													</div>
												))}
											</div>
										</div>

										<div className={fieldGroupClassName}>
											<Label
												htmlFor="service-notifications-from-name"
												className={fieldLabelClassName}
											>
												{i18n._("serviceEditor.notifications.fromName")}
											</Label>
											<Input
												id="service-notifications-from-name"
												value={
													normalizeServiceNotifications(
														editorService.notifications,
													).fromName
												}
												onChange={(event) =>
													setEditorService((current) =>
														current
															? {
																	...current,
																	notifications: {
																		...normalizeServiceNotifications(
																			current.notifications,
																		),
																		fromName: event.target.value,
																	},
																}
															: current,
													)
												}
												{...contentFieldProps(
													normalizeServiceNotifications(
														editorService.notifications,
													).fromName,
												)}
											/>
										</div>

										<div className={fieldGroupClassName}>
											<Label
												htmlFor="service-notifications-subject"
												className={fieldLabelClassName}
											>
												{i18n._("serviceEditor.notifications.subject")}
											</Label>
											<Input
												id="service-notifications-subject"
												value={
													normalizeServiceNotifications(
														editorService.notifications,
													).subject
												}
												onChange={(event) =>
													setEditorService((current) =>
														current
															? {
																	...current,
																	notifications: {
																		...normalizeServiceNotifications(
																			current.notifications,
																		),
																		subject: event.target.value,
																	},
																}
															: current,
													)
												}
												{...contentFieldProps(
													normalizeServiceNotifications(
														editorService.notifications,
													).subject,
												)}
											/>
										</div>

										<div className="flex min-h-48 flex-col gap-2 text-start">
											<Label
												htmlFor="service-notifications-body"
												className={fieldLabelClassName}
											>
												{i18n._("serviceEditor.notifications.body")}
											</Label>
											<Textarea
												id="service-notifications-body"
												value={
													normalizeServiceNotifications(
														editorService.notifications,
													).body
												}
												onChange={(event) =>
													setEditorService((current) =>
														current
															? {
																	...current,
																	notifications: {
																		...normalizeServiceNotifications(
																			current.notifications,
																		),
																		body: event.target.value,
																	},
																}
															: current,
													)
												}
												{...contentFieldProps(
													normalizeServiceNotifications(
														editorService.notifications,
													).body,
													"min-h-32 flex-1 overflow-y-auto",
												)}
											/>
										</div>
									</div>
								</TabsContent>
							</div>
						</Tabs>
					) : null}
				</DialogContent>
			</Dialog>

			<Dialog
				open={serviceToDelete !== null}
				onOpenChange={(open) => (!open ? closeDeleteDialog() : null)}
			>
				<DialogContent className="max-w-md">
					<DialogHeader>
						<DialogTitle>{t({ id: "delete", message: "Delete" })}</DialogTitle>
						<DialogDescription>
							{serviceToDelete
								? i18n._({
										id: "deleteAgentConfirm",
										message:
											"Are you sure you want to delete {name}? This action cannot be undone.",
										values: { name: serviceToDelete.name },
									})
								: ""}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={closeDeleteDialog}>
							{t({ id: "cancel", message: "Cancel" })}
						</Button>
						<Button
							variant="destructive"
							onClick={handleDeleteService}
							disabled={isDeleting}
						>
							{isDeleting
								? t({ id: "saving", message: "Saving..." })
								: t({ id: "delete", message: "Delete" })}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
